import { Flex, Grid, Heading, Link, minmax, repeat } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import qs from 'query-string';
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

// Shared components
import { CloudAppCard, ErrorBoundary } from '@exchange-frontends/components';
import { useStaticData } from '@exchange-frontends/custom-hooks';

/**
 * Featured Apps grid that shows 2 for mobile, 4 for tablet, 6 for desktop
 * @component
 * @category Featured
 * @prop {string} title - title of the section
 * @prop {string} viewAllHref - link to navigate to when the user clicks View All
 * @prop {string} viewAllAriaLabel - aria label for the View All link
 * @prop {string} type - the type of apps to get
 * @prop {string} [sortBy] - one of APP_TITLE | MOST_RECENT | PARTNER | RELEVANCE
 * @returns JSX.Element
 */
const FeaturedAppsGrid = ({
  title,
  viewAllHref,
  viewAllAriaLabel,
  loading,
  apps,
  encounteredError,
  errorMessage,
  analyticsId,
}) => {
  const { matchedBreakpoints } = useBreakpoint();
  const breakpoint = matchedBreakpoints[0];
  const productsLocs = useStaticData('hostApps');
  const { product = '' } = qs.parse(viewAllHref);

  const elementsNumber = useMemo(() => {
    switch (breakpoint) {
      case 'XXL':
      case 'XL':
        return 6;
      case 'L':
      case 'M':
        return 4;
      default:
        return 2;
    }
  }, [breakpoint]);

  return (
    <>
      <Flex justifyContent="space-between" alignItems="center" marginBottom="size-200">
        <Heading level={3} margin="0">
          {title}
        </Heading>
        <Link variant="secondary">
          <RouterLink aria-label={viewAllAriaLabel} to={viewAllHref} data-launchid={`${analyticsId ?? title} view all`}>
            View all
          </RouterLink>
        </Link>
      </Flex>
      <ErrorBoundary isError={encounteredError || !apps?.length} errorMessage={errorMessage}>
        {() => (
          <Grid
            gap="size-175"
            columns={repeat('auto-fit', minmax(matchedBreakpoints.includes('L') ? '395px' : '270px', '1fr'))}
          >
            {apps?.slice(0, elementsNumber)?.map((app, index) => (
              <CloudAppCard
                key={app ? `${app?.id}.${app?.name}.${app?.type}` : index}
                headerId={
                  app?.type?.toLowerCase() == 'ec'
                    ? 'Featured Apps for Experience Cloud'
                    : 'Featured Integrations for Acrobat Sign'
                }
                cloud={app?.type?.toLowerCase()}
                app={app}
                loading={loading}
                productsLocs={productsLocs}
                product={product}
                appType={app?.appType}
                publisher={app?.publisher}
              />
            ))}
          </Grid>
        )}
      </ErrorBoundary>
    </>
  );
};

export default FeaturedAppsGrid;
